// Libraries
import { Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { RatingsIcon } from '@atoms/index';
// Types
import { RatingInfoCompactedProps, VariantsConfig } from './RatingInfoCompacted.type';

/**
 * Component that renders rating information compactly.
 *
 * @component
 *
 * @param {RatingInfoCompactedProps} props - The props for the RatingInfoCompacted component.
 * @param {Rating} props.rating - The rating key, which should be one of the keys defined in the Rating enum.
 * @param {string} props.description - The description text for the rating.
 * @param {boolean} [props.isLoading=false] - The loading state for the rating. Default value false.
 * @param {boolean} [props.isLoadingDescription=false] - Indicates whether only the description is in a loading state. Defaults to false.
 * @param {boolean} [props.isLoadingRating=false] - Indicates whether only the rating is in a loading state. Defaults to false.
 * @param {RatingInfoSizesKeys} [props.ratingInfoSize='sm'] - The size to show a respective style.
 * @param {('row' | 'row-reverse')} [props.descriptionPosition='row'] - The flex direction for the layout of the rating icon and description.
 * @param {string} [props.descriptionColor] - The color of the description text.
 *
 * @returns {React.ReactElement} The RatingInfoCompacted component.
 */
export const RatingInfoCompacted = ({
  rating,
  description,
  isLoading = false,
  isLoadingDescription = false,
  isLoadingRating = false,
  ratingInfoSize = 'sm',
  descriptionPosition = 'row',
  descriptionColor,
}: RatingInfoCompactedProps) => {
  const { boxSize, fontSize } = VariantsConfig[ratingInfoSize];
  const isRatingLoaded = isLoading || isLoadingRating;
  const isDescriptionLoaded = isLoading || isLoadingDescription;

  return (
    <Flex direction={descriptionPosition} h={{ base: 5 }} gap={{ base: 1 }} alignItems='center'>
      {/* Skeleton to show a loading animation for the RatingsIcon */}
      <Skeleton isLoaded={!isRatingLoaded} fitContent>
        {/* Render the RatingsIcon with the given rating key, dark=true, and boxSize from the VariantsConfig */}
        <RatingsIcon rating={rating} dark={true} boxSize={boxSize} />
      </Skeleton>
      {/* Skeleton to show a loading animation for the Text description */}
      <Skeleton isLoaded={!isDescriptionLoaded} fitContent>
        {/* Render the description text with the given fontSize and lineHeight 'none' */}
        <Text fontSize={fontSize} lineHeight='none' color={descriptionColor}>
          {description}
        </Text>
      </Skeleton>
    </Flex>
  );
};
